import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, ArrowUpRightIcon } from 'lucide-react';
import { HeroLogoMark } from '../components/HeroLogoMark';
import { ConsultantList } from '../components/ConsultantList';
import { isFeatureEnabled } from '../utils/features';
import { ButtonLink } from '../components/ButtonLink';
import { Reveal } from '../components/Reveal';
import { MethodSteps } from '../components/MethodSteps';
import { CtaBand } from '../components/CtaBand';
import { practiceAreas } from '../data/expertise';
import { industries } from '../data/industries';
import { insights } from '../data/insights';
import { formatDate } from '../utils/date';
export function Home() {
  const [featured, ...others] = insights;
  return <>
      <section className="relative overflow-hidden bg-midnight-950 text-champagne-50" data-id="element-321" data-mp="2dfdu9xx8z0vc">
        <div className="mx-auto grid max-w-site items-center gap-10 px-6 pb-20 pt-14 lg:grid-cols-12 lg:px-10 lg:pb-28 lg:pt-20" data-id="element-322" data-mp="3kxd276lgy509">
          <div className="lg:col-span-7" data-id="element-323" data-mp="30wjfqyjbda93">
            <p className="text-xs font-medium uppercase tracking-[0.28em] text-champagne-400" data-id="element-324" data-mp="30w9l2xnbqex2">Retained Executive Search</p>
            <h1 className="mt-6 font-serif text-5xl leading-[1.05] md:text-6xl lg:text-[4.25rem]" data-id="element-325" data-mp="2vvltoav6nw2b">Leadership that defines what comes next.</h1>
            <p className="mt-7 max-w-xl text-lg leading-relaxed text-champagne-100/75" data-id="element-326" data-mp="2mxs0v9wmnkjw">
              Qorvessa Executive Partners is a retained executive search firm placing board members, chief executives, and senior
              leaders with organizations at moments of consequence.
            </p>
            <div className="mt-10 flex flex-wrap gap-4" data-id="element-327" data-mp="287cgfxc1cgms">
              <ButtonLink to="/contact?type=employer" withArrow data-id="element-328" data-mp="285rjk1290llr">
                Discuss Your Hiring Needs
              </ButtonLink>
              <ButtonLink to="/contact?type=candidate" variant="outlineDark" data-id="element-329" data-mp="1z7xsp5n96k5z">
                Introduce Yourself
              </ButtonLink>
            </div>
          </div>
          <div className="lg:col-span-5" data-id="element-330" data-mp="1kkqn7fgjeeeh">
            <HeroLogoMark data-id="element-331" data-mp="1kjnvx6fay9y1" />
          </div>
        </div>
      </section>

      <section className="bg-ivory" data-id="element-332" data-mp="1om2ctkzeuzmo">
        <div className="mx-auto grid max-w-site gap-10 px-6 py-24 lg:grid-cols-12 lg:px-10 lg:py-32" data-id="element-333" data-mp="1on3rdzfmy495">
          <Reveal className="lg:col-span-7" data-id="element-334" data-mp="1ao43itzy5te1">
            <h2 className="font-serif text-4xl leading-tight text-midnight-900 md:text-5xl" data-id="element-335" data-mp="1flmboi6544kz">
              We search for the leaders others overlook — and recommend only those we would stand behind.
            </h2>
          </Reveal>
          <Reveal delay={0.06} className="lg:col-span-4 lg:col-start-9 lg:pt-3" data-id="element-336" data-mp="2899txqjxuj4g">
            <p className="text-[17px] leading-relaxed text-midnight-900/70" data-id="element-337" data-mp="1tlx7lzmb49d0">
              Every mandate is partner-led, research-driven, and handled with complete discretion. We work with a deliberately
              small number of clients so each search receives the senior attention it deserves.
            </p>
            <Link to="/about" className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-midnight-900 underline decoration-champagne-500 underline-offset-[6px]" data-id="element-338" data-mp="2r1uk30896a4k">
              About the firm
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-339" data-mp="alo3l43zdjqr" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-champagne-50" data-id="element-340" data-mp="og8d8um60uvn">
        <div className="mx-auto max-w-site px-6 py-24 lg:px-10 lg:py-32" data-id="element-341" data-mp="kcaewm4nzo3n">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between" data-id="element-342" data-mp="3ssc74pjdtyub">
            <h2 className="font-serif text-4xl text-midnight-900 md:text-5xl" data-id="element-343" data-mp="3nt94fbqvk4pi">Our expertise</h2>
            <Link to="/expertise" className="inline-flex items-center gap-2 text-sm font-medium text-midnight-900 underline decoration-champagne-500 underline-offset-[6px]" data-id="element-344" data-mp="3nvlru4fej79y">
              All practice areas
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-345" data-mp="1h2t9a8dl5v5j" />
            </Link>
          </div>
          <ul className="mt-12 border-b border-midnight-900/15" data-id="element-346" data-mp="1c3yoj0w25wis">
            {practiceAreas.map(area => <li key={area.id} className="border-t border-midnight-900/15" data-id="element-347" data-mp="27eeog68o4tft">
                <Link to={`/expertise#${area.id}`} className="group grid items-baseline gap-3 py-7 md:grid-cols-12 md:gap-8" data-id="element-348" data-mp="2cczskgr1jx9j">
                  <h3 className="font-serif text-2xl text-midnight-900 transition-colors duration-150 ease-out group-hover:text-midnight-600 md:col-span-4 md:text-3xl" data-id="element-349" data-mp="2hbt5a6yi7k8n">
                    {area.title}
                  </h3>
                  <p className="text-[15px] leading-relaxed text-midnight-900/70 md:col-span-7" data-id="element-350" data-mp="2qbzcjb3lznzd">{area.summary}</p>
                  <ArrowUpRightIcon className="hidden h-5 w-5 justify-self-end text-champagne-600 transition-transform duration-150 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5 md:col-span-1 md:block" aria-hidden="true" data-id="element-351" data-mp="2va0z4608u5fl" />
                </Link>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="bg-midnight-950 text-champagne-50" data-id="element-352" data-mp="3e223fnvsenwe">
        <div className="mx-auto max-w-site px-6 py-24 lg:px-10 lg:py-32" data-id="element-353" data-mp="3e2leqlqxme1g">
          <div className="grid gap-8 lg:grid-cols-12" data-id="element-354" data-mp="12friyoo7xab4">
            <h2 className="font-serif text-4xl md:text-5xl lg:col-span-5" data-id="element-355" data-mp="ydmstzwzavt8">Industries we serve</h2>
            <p className="text-[17px] leading-relaxed text-champagne-100/70 lg:col-span-5 lg:col-start-8" data-id="element-356" data-mp="te1tvpht6ngi">
              Deep sector networks paired with cross-industry perspective — so we can bring you leaders who know your market, and
              those who can transform it.
            </p>
          </div>
          <ul className="mt-14 grid gap-x-10 sm:grid-cols-2 lg:grid-cols-4" data-id="element-357" data-mp="1h3cmuksx3dk3">
            {industries.map(industry => <li key={industry.id} className="border-t border-champagne-100/15 py-6" data-id="element-358" data-mp="1cc4ezt78k091">
                <Link to={`/industries#${industry.id}`} className="font-serif text-2xl text-champagne-50 transition-colors duration-150 ease-out hover:text-champagne-300" data-id="element-359" data-mp="17eup6cfxpmko">
                  {industry.name}
                </Link>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="bg-ivory" data-id="element-360" data-mp="3ubncuhsh4qra">
        <div className="mx-auto max-w-site px-6 py-24 lg:px-10 lg:py-32" data-id="element-361" data-mp="3ubdu9f9rdlvm">
          <div className="max-w-2xl" data-id="element-362" data-mp="25i6qnt4advz">
            <h2 className="font-serif text-4xl text-midnight-900 md:text-5xl" data-id="element-363" data-mp="28mflyhj1xsz">A search methodology built on evidence</h2>
            <p className="mt-5 text-[17px] leading-relaxed text-midnight-900/70" data-id="element-364" data-mp="3pjm61be2rs8n">
              Six disciplined stages take every mandate from first conversation to a successful first year in role.
            </p>
          </div>
          <div className="mt-14" data-id="element-365" data-mp="3fmr4skqf3dqo">
            <MethodSteps data-id="element-366" data-mp="3fjlgh7pm4fw0" />
          </div>
        </div>
      </section>

      {isFeatureEnabled('consultants') && <section className="bg-champagne-50" data-id="element-367" data-mp="36l02lpv35p1k">
          <div className="mx-auto max-w-site px-6 py-24 lg:px-10 lg:py-32" data-id="element-368" data-mp="36m1hk5f82vok">
            <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between" data-id="element-369" data-mp="zu0p54o8ip31">
              <h2 className="font-serif text-4xl text-midnight-900 md:text-5xl" data-id="element-370" data-mp="14u6j4rhz8noa">Meet our consultants</h2>
              <Link to="/consultants" className="inline-flex items-center gap-2 text-sm font-medium text-midnight-900 underline decoration-champagne-500 underline-offset-[6px]" data-id="element-371" data-mp="1ex1yzfqdhtmu">
                Our team
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-372" data-mp="1nq5ahihgjweu" />
              </Link>
            </div>
            <div className="mt-12" data-id="element-373" data-mp="1iy42w9v8itv5">
              <ConsultantList data-id="element-374" data-mp="1ix2fd5x18ewj" />
            </div>
          </div>
        </section>}

      <section className="bg-ivory py-24 lg:py-32" data-id="element-375" data-mp="1xm8dsro1q13d">
        <div className="mx-auto grid max-w-site gap-6 px-6 lg:grid-cols-2 lg:px-10" data-id="element-376" data-mp="1sphyw7nz9bjj">
          <Reveal className="flex h-full flex-col rounded-lg bg-midnight-900 p-10 text-champagne-50 lg:p-14" data-id="element-377" data-mp="26ohkt7k3vcc0">
            <p className="text-sm font-medium text-champagne-300" data-id="element-378" data-mp="22mmoyiky1r5m">For Employers</p>
            <h2 className="mt-4 font-serif text-4xl leading-tight" data-id="element-379" data-mp="cagooufxx3fm">Build the leadership team your strategy requires.</h2>
            <p className="mt-5 text-[16px] leading-relaxed text-champagne-100/70" data-id="element-380" data-mp="89wt3vb0m7vl">
              Partner-led retained search for board, C-suite, and senior roles, with candid market intelligence at every stage.
            </p>
            <div className="mt-auto flex flex-wrap gap-4 pt-10" data-id="element-381" data-mp="11rleym21bbgx">
              <ButtonLink to="/contact?type=employer" withArrow data-id="element-382" data-mp="16jcy4biv2ex1">
                Discuss Your Hiring Needs
              </ButtonLink>
              <ButtonLink to="/employers" variant="outlineDark" data-id="element-383" data-mp="1akygudi2tnf5">
                How we partner
              </ButtonLink>
            </div>
          </Reveal>
          <Reveal delay={0.06} className="flex h-full flex-col rounded-lg bg-champagne-100 p-10 text-midnight-900 lg:p-14" data-id="element-384" data-mp="itsavgfocvhy">
            <p className="text-sm font-medium text-champagne-600" data-id="element-385" data-mp="muuavzhf1bkz">For Candidates</p>
            <h2 className="mt-4 font-serif text-4xl leading-tight" data-id="element-386" data-mp="rv8m1qebd1n8">Explore your next leadership chapter, in confidence.</h2>
            <p className="mt-5 text-[16px] leading-relaxed text-midnight-900/70" data-id="element-387" data-mp="1ya9916ld29ho">
              Many of our mandates are never advertised. Introduce yourself and we’ll be in touch when the right opportunity arises.
            </p>
            <div className="mt-auto flex flex-wrap gap-4 pt-10" data-id="element-388" data-mp="2dg6prvhbrd22">
              <ButtonLink to="/contact?type=candidate" variant="solidDark" withArrow data-id="element-389" data-mp="28gahhk1lgzgp">
                Introduce Yourself
              </ButtonLink>
              <ButtonLink to="/opportunities" variant="outlineLight" data-id="element-390" data-mp="1zmnog1oruab7">
                Leadership Opportunities
              </ButtonLink>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-midnight-900/10 bg-champagne-50" data-id="element-391" data-mp="1pohrzzlqhwdq">
        <div className="mx-auto max-w-site px-6 py-24 lg:px-10 lg:py-32" data-id="element-392" data-mp="1pqkpt5t4ott0">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between" data-id="element-393" data-mp="1kpwz6l8taezb">
            <h2 className="font-serif text-4xl text-midnight-900 md:text-5xl" data-id="element-394" data-mp="1gp4nllcgvxzv">Insights</h2>
            <Link to="/insights" className="inline-flex items-center gap-2 text-sm font-medium text-midnight-900 underline decoration-champagne-500 underline-offset-[6px]" data-id="element-395" data-mp="3f2edh3boa4rt">
              All insights
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-396" data-mp="cna4km4fw7qa" />
            </Link>
          </div>
          <div className="mt-12 grid gap-10 lg:grid-cols-12" data-id="element-397" data-mp="8m8461ysdpmq">
            <Reveal className="lg:col-span-7" data-id="element-398" data-mp="mmiyjezb1mcn">
              <Link to={`/insights/${featured.slug}`} className="group flex h-full flex-col rounded-lg bg-midnight-900 p-10 text-champagne-50 lg:p-12" data-id="element-399" data-mp="mm96llup954b">
                <p className="text-sm text-champagne-300" data-id="element-400" data-mp="3q2ybl3emw6rv">
                  {featured.category} · {formatDate(featured.date)}
                </p>
                <h3 className="mt-6 font-serif text-4xl leading-tight transition-colors duration-150 ease-out group-hover:text-champagne-200" data-id="element-401" data-mp="3l67xglme3l93">
                  {featured.title}
                </h3>
                <p className="mt-5 max-w-lg text-[16px] leading-relaxed text-champagne-100/70" data-id="element-402" data-mp="3v19m7gcr6lb5">{featured.excerpt}</p>
                <span className="mt-auto inline-flex items-center gap-2 pt-10 text-sm font-medium text-champagne-200" data-id="element-403" data-mp="37ax8fouttucn">
                  Read article <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-404" data-mp="1hvcppns8ac9e" />
                </span>
              </Link>
            </Reveal>
            <ul className="lg:col-span-5" data-id="element-405" data-mp="18vzd4m8t57kg">
              {others.slice(0, 3).map(article => <li key={article.slug} className="border-t border-midnight-900/15" data-id="element-406" data-mp="1cw03n596l97v">
                  <Link to={`/insights/${article.slug}`} className="group block py-6" data-id="element-407" data-mp="20mm162lk7b3m">
                    <p className="text-sm text-midnight-900/55" data-id="element-408" data-mp="25lgptl67jtvn">
                      {article.category} · {article.readTime}
                    </p>
                    <h3 className="mt-2 font-serif text-2xl leading-snug text-midnight-900 transition-colors duration-150 ease-out group-hover:text-midnight-600" data-id="element-409" data-mp="1rkwbp2b8mm8h">
                      {article.title}
                    </h3>
                  </Link>
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      <CtaBand data-id="element-410" data-mp="2murouamptj1q" />
    </>;
}